import {
  type Control,
  Controller,
  type FieldErrors,
  type UseFormRegister,
} from 'react-hook-form'

import AtomCheckbox from '@/components/Atoms/AtomCheckbox/AtomCheckbox'
import AtomFormInput from '@/components/Atoms/AtomFormInput/AtomFormInput'
import AtomSelect from '@/components/Atoms/AtomSelect/AtomSelect'
import AtomToggleSwitch from '@/components/Atoms/AtomToggleSwitch/AtomToggleSwitch'
import HoverElement from '@/components/Atoms/HoverElement/HoverElement'
import type {
  FormInputControlType,
  FormInputNameType,
  FormInputRegisterType,
  OptionType,
  SelectFormControlType,
} from '@/types'

import {
  STORE_DAYS_MASK,
  STORE_TIME_MASK,
  type StoreCreationFormType,
} from './StoreCreationModal.config'
import cn from './StoreCreationModal.module.scss'

type StoreCreationFieldsProps = {
  control: Control<StoreCreationFormType>
  register: UseFormRegister<StoreCreationFormType>
  errors: FieldErrors<StoreCreationFormType>
  supplierOptions: OptionType[]
  showSupplierSelect: boolean
  isOrganisationSelected: boolean
}

function StoreCreationFields({
  control,
  register,
  errors,
  supplierOptions,
  showSupplierSelect,
  isOrganisationSelected,
}: StoreCreationFieldsProps) {
  const renderInput = (
    name: FormInputNameType,
    title: string,
    options: {
      required?: boolean
      placeholder?: string
      mask?: typeof STORE_TIME_MASK | typeof STORE_DAYS_MASK
    }
  ) => (
    <AtomFormInput
      layout="stacked"
      idPrefix="store-"
      location="storeCreation"
      isOrganisationSelected={isOrganisationSelected}
      name={name}
      title={title}
      placeholder={options.placeholder}
      required={options.required}
      mask={options.mask}
      inputMode={options.mask ? 'numeric' : undefined}
      errors={errors}
      register={register as FormInputRegisterType}
      control={control as FormInputControlType}
    />
  )

  return (
    <>
      {showSupplierSelect && (
        <HoverElement>
          <AtomSelect
            layout="stacked"
            fullWidth
            showSearch
            openByHover={false}
            isOrganisationSelected={isOrganisationSelected}
            title="Поставщик"
            placeholder="Не установлен"
            options={supplierOptions}
            formName="supplier"
            formErrors={errors}
            formControl={control as SelectFormControlType}
          />
        </HoverElement>
      )}
      {renderInput('description', 'Наименование', {
        required: true,
        placeholder: 'Например: Основной склад',
      })}
      {renderInput('address', 'Адрес', {})}
      {renderInput('contactPersons', 'Контактные лица', {})}
      {renderInput('comment', 'Комментарий', {})}

      {renderInput('etaMinDays', 'Мин. срок, дней', {
        required: true,
        placeholder: '0',
        mask: STORE_DAYS_MASK,
      })}
      {renderInput('etaMaxDays', 'Макс. срок, дней', {
        required: true,
        placeholder: '0',
        mask: STORE_DAYS_MASK,
      })}
      {renderInput('etaCutoffTime', 'Время отсечки', {
        required: true,
        placeholder: 'чч:мм',
        mask: STORE_TIME_MASK,
      })}

      <div className={cn.flags}>
        <Controller
          control={control}
          name="isRemote"
          render={({ field }) => (
            <AtomCheckbox
              checked={field.value}
              onChange={field.onChange}
              isOrganisationSelected={isOrganisationSelected}
            >
              Удалённый склад
            </AtomCheckbox>
          )}
        />
        <Controller
          control={control}
          name="isActive"
          render={({ field }) => (
            <AtomToggleSwitch
              checked={field.value}
              onChange={field.onChange}
              isOrganisationSelected={isOrganisationSelected}
            >
              Склад активен
            </AtomToggleSwitch>
          )}
        />
      </div>
    </>
  )
}

export default StoreCreationFields
